import { Component, ElementRef, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { CustomValidator } from 'projects/shared/src/helpers/custom.validator';
import { Patterns } from 'projects/shared/src/helpers/patterns';
import { SwalService } from 'projects/shared/src/services/swal.service';
import { DirtyComponent } from 'projects/shared/src/helpers/CanDeactivate/dirty-component';
import { UserService } from '../../../features/user-management/internal-users/services/users.service';
import { UserTypeTabEnum } from '../../../features/user-management/internal-users/models/enums/userTypeTabEnum';
import { LookupsService } from 'projects/shared/src/services/lookups.service';
import { RoleType } from '../../../features/user-management/roles/models/enums/roleType';

export interface DialogData {
  userTypeEnum: UserTypeTabEnum,
  roleType: RoleType,
  record: any,
}


@Component({
  selector: 'app-add-user-form',
  standalone: false,
  templateUrl: './add-user-form.component.html'
})
export class AddUserFormComponent extends DirtyComponent {
  override formGroup: FormGroup = new FormGroup({});

  loading = false;
  dataLoading = false;
  isSubmitted = false;
  isUpdate!: boolean;

  systemPermissions = [];
  loadingPermissions = true;
  selectedPermissions: any = [];

  searchText = '';

  details!: any;

  roles: any = [];
  rolesLoading = false;

  // image file
  logo: any;
  logoSrc!: string;
  maxFileSize = 2;
  fileTypeError = false;
  fileSizeError = false;
  removeUserImage = false;


  constructor(override dialogRef: MatDialogRef<AddUserFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private formBuilder: FormBuilder,
    translate: TranslateService,
    private service: UserService,
    private el: ElementRef,
    private lookupsService: LookupsService,
    private swalService: SwalService) {
    super(translate)
  }

  ngOnInit(): void {
    console.log(this.data.record);

    this.getRoles();
    this.isUpdate = this.data.record ? true : false;
    this.buildForm();
    if (this.isUpdate) {
      this.bindDataToUpdate();
    }
  }

  buildForm() {
    this.formGroup = this.formBuilder.group({
      userId: [],
      Name: [null, [
        CustomValidator.noWhiteSpace,
        Validators.pattern(Patterns.noSpecialTxt),
        Validators.maxLength(150),
        Validators.minLength(3)
      ]],
      Role: [null, [
        Validators.required,
      ]],
      PhoneNumber: [null, [
        Validators.required,
        Validators.pattern(/^5\d{8,11}$/)
      ]],
      PhoneExtension: ['+966', [
        Validators.required,
      ]],
      Email: [null, [
        Validators.required,
        Validators.pattern(Patterns.email),
        Validators.minLength(5)
      ]],
    });
  }

  hasError(controlName: string, errorName: string) {
    return this.formGroup.controls[controlName].hasError(errorName);
  }

  getRoles() {
    this.rolesLoading = true;
    this.lookupsService.getRolesByRoleType(this.data.roleType).subscribe((res) => {
      this.roles = res;
      this.rolesLoading = false;
    })
  }

  getDetails() {
    this.dataLoading = true;
    this.service.getUserDetails(this.data.record.id).subscribe((res) => {
      this.details = res;
      this.dataLoading = false;
      this.bindDataToUpdate();
    })
  }

  bindDataToUpdate() {
    this.formGroup.patchValue({
      Name: this.data.record.name,
      Role: this.data.record.role,
      PhoneNumber: this.data.record.phoneNumber,
      PhoneExtension: this.data.record.phoneExtension,
      Email: this.data.record.email,
    });
    this.logoSrc = this.data.record.userImage
  }


  saveRecord() {
    this.isSubmitted = true;
    if (this.formGroup.valid) {
      this.loading = true;
      // this.formGroup.controls['Role'].setValue(this.roles.find((role: any) => role.id == this.formGroup.controls['Role'].value)?.roleName)

      let data = this.formGroup.value;
      data.userTypeEnum = this.data.userTypeEnum
      data.removeUserImage = this.removeUserImage

      let title = this.translate.instant('users.New user added successfully');
      let text = this.translate.instant('users.Waiting for the user to activate his account');

      if (this.isUpdate) {
        data.userId = this.data.record.userId;
        title = this.translate.instant( this.data.userTypeEnum == UserTypeTabEnum.INTERNAL_USER ? 'swalMsg.updatedsuccessfully' : 'swalMsg.externalUserDataUpdatedSuccessfully');
        text = '';
      }
      // data.Role = this.roles.find((role: any) => role.id == this.formGroup.controls['Role'].value)?.name
      const formData = new FormData();
      if (this.logo) {
        formData.append('files', this.logo);
      }
      formData.append('data', JSON.stringify(data));

      this.service.addUser(formData, data.userId).subscribe(() => {
        this.loading = false;
        Swal.fire({
          title: title,
          text: text,
          imageUrl: './assets/images/icons/success.svg',
          confirmButtonText: this.translate.instant('swalMsg.Ok')
        });
        this.dialogRef.close([true]);
      }, error => {
        this.loading = false;
        this.handleErrors(error.detail)
      });
    } else {
      this.formGroup.markAllAsTouched();
      this.scrollToFirstInvalidControl();
    }
  }

  handleErrors(errors: any) {
    errors = JSON.parse(errors);
    errors.ErrorDTO.forEach((error: any) => {
      if (error.ErrorCode === 'EmailExist') {
        this.formGroup.controls['Email'].setErrors({ notUniqe: true });
      } else if (error.ErrorCode === 'PhoneNumberExist') {
        this.formGroup.controls['PhoneNumber'].setErrors({ notUniqe: true });
      } else {
        this.swalService.swalFailed(error.Error);
      }
    });
    setTimeout(() => {
      this.scrollToFirstInvalidControl();
    }, 100);
  }

  // File preview with validation
  changeFileListener($event: any): void {
    const file: File = $event.target.files[0];
    if (file && this.validateFile(file)) {
      this.readThis(file);
      this.logo = file;
      this.removeUserImage = false
    } else {
      this.logoSrc = this.logoSrc;
      this.logo = this.logo;
    }
  }
  readThis(file: File): void {
    const myReader: FileReader = new FileReader();
    myReader.readAsDataURL(file);
    myReader.onloadend = (e) => {
      this.logoSrc = myReader.result as string;
    };
  }
  validateFile(file: File): any {
    if (this.fileType(file.name)) {
      this.fileTypeError = false;
      if ((file.size / (1024 * 1024)) <= this.maxFileSize) {
        this.fileSizeError = false;
      } else {
        this.fileSizeError = true;
        return false;
      }
    } else {
      this.fileTypeError = true;
      return false;
    }
    return true;
  }
  fileType(fileName: string): any {
    const extension = fileName.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'jpeg':
      case 'jpg':
        return 'jpg';
      case 'png':
        return 'png';
      default:
        return false;
    }
  }

  removeImg($event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.logoSrc = ''
    this.removeUserImage = true
  }


  private scrollToFirstInvalidControl() {
    const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector(
      'form .ng-invalid'
    );
    firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }
}
